/**
 * @fileoverview useMonthBalance - Monthly balance data hook.
 *
 * Fetches and manages the overall balance information for a selected month,
 * including starting balance, current balance, ending balance, and close status.
 *
 * @description This hook provides:
 * - Automatic balance loading when month changes
 * - Balance refresh capability
 * - Month close status tracking
 * - Flexible date format handling (Date object or YYYY-MM string)
 * - Automatic budget context subscription
 *
 * Balance flow:
 * - Starting Balance: Ending balance from previous month
 * - Current Balance: Starting + all income - all spending (real-time)
 * - Ending Balance: Balance when month is closed (snapshot)
 *
 * @module hooks/useMonthBalance
 */
import { useState, useEffect, useCallback } from 'react';
import { monthAPI, onBudgetContextChange } from '../services/api';
import { useRealtime } from '../contexts/RealtimeContext';

/**
 * Hook for managing month balance data.
 *
 * @param {Date|string} selectedMonth - The month as a Date object or YYYY-MM string
 * @returns {Object} Balance data and operations
 * @returns {number} return.startingBalance - Balance at start of month
 * @returns {number} return.currentBalance - Real-time balance
 * @returns {number} return.endingBalance - Balance when month closed
 * @returns {boolean} return.isClosed - Whether month is closed
 * @returns {Error|null} return.error - Any fetch error
 * @returns {boolean} return.isLoading - Whether balance is loading
 * @returns {Function} return.refreshBalance - Manually refresh balance
 *
 * @example
 * const balance = useMonthBalance(selectedMonth);
 * console.log(`Current: ${balance.currentBalance}`);
 * if (balance.isClosed) console.log('Month is closed');
 */
export function useMonthBalance(selectedMonth) {
    const [balanceData, setBalanceData] = useState({
        startingBalance: 0,
        currentBalance: 0,
        endingBalance: 0,
        isClosed: false
    });
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Realtime context for live balance updates
    const { subscribeToEvent, isConnected } = useRealtime();

    // Convert Date object to YYYY-MM format
    const getMonthKey = useCallback((date) => {
        if (!date) return null;

        if (typeof date === 'string') {
            // If already a string, extract YYYY-MM
            const [year, month] = date.split('-');
            return `${year}-${month}`;
        }

        if (date instanceof Date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            return `${year}-${month}`;
        }

        return null;
    }, []);

    // Fetch balance from API
    const fetchBalance = useCallback(async () => {
        const monthKey = getMonthKey(selectedMonth);
        if (!monthKey) return;

        // Wait for budget context to be set before loading
        const { transactionAPI } = require('../services/api');
        if (!transactionAPI.getCurrentBudgetId()) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const data = await monthAPI.getBalance(monthKey);
            setBalanceData({
                startingBalance: data.startingBalance || 0,
                currentBalance: data.currentBalance || 0,
                endingBalance: data.endingBalance || 0,
                isClosed: data.isClosed || false
            });
        } catch (err) {
            // Error handling
            setError('Error updating balance');
            // Keep the old balance data on error
        } finally {
            setIsLoading(false);
        }
    }, [selectedMonth, getMonthKey]);

    // Fetch balance when month changes
    useEffect(() => {
        fetchBalance();

        // Subscribe to budget context changes and reload when it's set
        const unsubscribe = onBudgetContextChange((budgetId) => {
            if (budgetId) {
                fetchBalance();
            }
        });

        return unsubscribe;
    }, [fetchBalance]);

    // Subscribe to realtime balance updates
    useEffect(() => {
        if (!subscribeToEvent) return;

        const unsubscribe = subscribeToEvent('balance:updated', (data) => {
            // Refresh balance when notified of changes
            // The event includes the monthKey that was affected
            const currentMonthKey = getMonthKey(selectedMonth);
            if (data.monthKey === currentMonthKey || !data.monthKey) {
                fetchBalance();
            }
        });

        return unsubscribe;
    // isConnected in deps ensures we re-subscribe when socket connects
    }, [subscribeToEvent, selectedMonth, getMonthKey, fetchBalance, isConnected]);

    return {
        ...balanceData,
        error,
        isLoading,
        refreshBalance: fetchBalance
    };
}
